import React from "react";
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    Font,
} from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import logo from "../../../images/ensss_logo.png";

/* =========================================================
 * POLICES
 * ======================================================= */

Font.register({
    family: "Louguiya",
    src: "/fonts/Louguiya.ttf",
    fontWeight: "normal",
});

Font.register({
    family: "Louguiya",
    src: "/fonts/Louguiya Bold.ttf",
    fontWeight: "bold",
});

Font.register({
    family: "LouguiyaFR",
    src: "/fonts/LouguiyaFR.ttf",
    fontWeight: "normal",
});

Font.register({
    family: "LouguiyaFR",
    src: "/fonts/LouguiyaFR Bold.ttf",
    fontWeight: "bold",
});

/* =========================================================
 * HELPERS
 * ======================================================= */

/**
 * Détecte la présence de caractères arabes.
 *
 * On ne dépend pas uniquement de la langue de l'interface :
 * une donnée peut être arabe alors que l'interface est en français.
 */
const containsArabic = (value) => {
    if (value === null || value === undefined) {
        return false;
    }

    return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(
        String(value)
    );
};

/**
 * Détecte si la langue courante est arabe.
 */
const isArabicLanguage = (language) => {
    return String(language || "")
        .toLowerCase()
        .startsWith("ar");
};

/**
 * Retourne le style approprié selon le contenu.
 *
 * - Arabe -> Louguiya
 * - Français / latin -> LouguiyaFR
 */
const getTextStyle = (value, baseStyle, bold = false) => {
    const arabic = containsArabic(value);

    return [
        baseStyle,
        {
            fontFamily: arabic ? "Louguiya" : "LouguiyaFR",
            fontWeight: bold ? "bold" : "normal",
        },
        arabic && styles.arabicText,
    ];
};

/**
 * Format date courte selon la langue.
 */
const formatDate = (date, language = "fr") => {
    if (!date) {
        return "-";
    }

    try {
        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) {
            return "-";
        }

        return new Intl.DateTimeFormat(
            isArabicLanguage(language) ? "ar-TN" : "fr-FR",
            {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            }
        ).format(parsedDate);
    } catch (error) {
        return "-";
    }
};

/**
 * Format date longue selon la langue.
 */
const formatLongDate = (date, language = "fr") => {
    if (!date) {
        return "-";
    }

    try {
        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) {
            return "-";
        }

        return new Intl.DateTimeFormat(
            isArabicLanguage(language) ? "ar-TN" : "fr-FR",
            {
                day: "numeric",
                month: "long",
                year: "numeric",
            }
        ).format(parsedDate);
    } catch (error) {
        return "-";
    }
};

/**
 * Convertit une valeur en nombre.
 */
const toNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
};

/**
 * Calcule les statistiques du relevé.
 */
const calculateStats = (rows = []) => {
    const total = rows.length;

    if (!total) {
        return {
            total: 0,
            passed: 0,
            failed: 0,
            successRate: 0,
            average: 0,
        };
    }

    const passed = rows.filter(
        (row) => Boolean(row?.passed)
    ).length;

    const failed = total - passed;

    const percentages = rows
        .map((row) => toNumber(row?.scorePercentage))
        .filter((value) => Number.isFinite(value));

    const average = percentages.length
        ? percentages.reduce(
            (sum, value) => sum + value,
            0
        ) / percentages.length
        : 0;

    return {
        total,
        passed,
        failed,
        successRate: (passed / total) * 100,
        average,
    };
};

/**
 * Formate les nombres selon la langue.
 */
const formatNumber = (
    value,
    language = "fr",
    maximumFractionDigits = 2
) => {
    try {
        return new Intl.NumberFormat(
            isArabicLanguage(language) ? "ar-TN" : "fr-FR",
            {
                minimumFractionDigits: 0,
                maximumFractionDigits,
            }
        ).format(value);
    } catch (error) {
        return String(value);
    }
};

/* =========================================================
 * BADGE
 * ======================================================= */

const Badge = ({
    children,
    variant = "default",
}) => {
    let badgeStyle = styles.badgeDefault;

    if (variant === "success") {
        badgeStyle = styles.badgeSuccess;
    }

    if (variant === "danger") {
        badgeStyle = styles.badgeDanger;
    }

    if (variant === "warning") {
        badgeStyle = styles.badgeWarning;
    }

    const arabic = containsArabic(children);

    return (
        <View style={[styles.badge, badgeStyle]}>
            <Text
                style={[
                    styles.badgeText,
                    {
                        fontFamily: arabic
                            ? "Louguiya"
                            : "LouguiyaFR",
                        fontWeight: "bold",
                    },
                    arabic && styles.arabicText,
                ]}
            >
                {children}
            </Text>
        </View>
    );
};

/* =========================================================
 * HEADER
 * ======================================================= */

const TranscriptHeader = ({
    transcript,
    t,
    language,
    isArabic,
}) => {
    /*
     * Le nom réel de l'établissement provient des données.
     * S'il n'est pas disponible, on utilise une clé i18n.
     *
     * À ajouter dans les fichiers de traduction :
     *
     * pages.results.institutionName
     */
    const schoolName =
        transcript?.institutionName ||
        t("pages.results.institutionName", {
            defaultValue:
                "École Nationale Supérieure de Sécurité Sociale",
        });

    const documentDate = formatLongDate(
        new Date(),
        language
    );

    const documentLabel = t(
        "pages.results.document",
        {
            defaultValue: "DOCUMENT",
        }
    );

    const documentTitle = t(
        "pages.results.releve",
        {
            defaultValue: "Relevé de notes",
        }
    );

    const academicYearLabel = t(
        "pages.results.academicYear",
        {
            defaultValue: "Année universitaire",
        }
    );

    return (
        <>
            <View style={styles.topBar} />

            <View style={styles.header}>
                <View
                    style={[
                        styles.headerRow,
                        isArabic && styles.headerRowRtl,
                    ]}
                >
                    {/* =================================================
                     * LOGO + ECOLE
                     * ================================================= */}

                    <View
                        style={[
                            styles.institutionSection,
                            isArabic &&
                            styles.institutionSectionRtl,
                        ]}
                    >
                        <Image
                            src={logo}
                            style={[
                                styles.logo,
                                isArabic &&
                                styles.logoRtl,
                            ]}
                        />

                        <View
                            style={[
                                styles.institutionText,
                                isArabic &&
                                styles.institutionTextRtl,
                            ]}
                        >
                            <Text
                                style={getTextStyle(
                                    schoolName,
                                    styles.institutionName,
                                    true
                                )}
                            >
                                {schoolName}
                            </Text>
                        </View>
                    </View>

                    {/* =================================================
                     * INFORMATIONS DOCUMENT
                     * ================================================= */}

                    <View
                        style={[
                            styles.documentInfo,
                            isArabic &&
                            styles.documentInfoRtl,
                        ]}
                    >
                        <Text
                            style={getTextStyle(
                                documentLabel,
                                styles.documentLabel,
                                true
                            )}
                        >
                            {documentLabel}
                        </Text>

                        <Text
                            style={getTextStyle(
                                documentTitle,
                                styles.documentTitle,
                                true
                            )}
                        >
                            {documentTitle}
                        </Text>

                        <Text
                            style={getTextStyle(
                                documentDate,
                                styles.documentDate
                            )}
                        >
                            {documentDate}
                        </Text>
                    </View>
                </View>
            </View>

            {/* =====================================================
             * TITRE
             * =================================================== */}

            <View
                style={[
                    styles.titleSection,
                    isArabic &&
                    styles.titleSectionRtl,
                ]}
            >
                <Text
                    style={getTextStyle(
                        documentTitle,
                        styles.title,
                        true
                    )}
                >
                    {documentTitle}
                </Text>

                {transcript?.academicYear && (
                    <Text
                        style={getTextStyle(
                            `${academicYearLabel} : ${transcript.academicYear}`,
                            styles.academicYear
                        )}
                    >
                        {academicYearLabel} :{" "}
                        {transcript.academicYear}
                    </Text>
                )}
            </View>
        </>
    );
};

const StudentCard = ({
    transcript,
    t,
    isArabic,
}) => {
    const student = transcript?.student || {};

    /*
     * ============================================================
     * DONNÉES ÉTUDIANT
     * ============================================================
     */

    const studentName =
        student.fullName || "-";

    const inscriptionNumber =
        student.registrationNumber || "-";

    const nni =
        student.nni || "-";

    const email =
        student.email || "-";

    const nationality =
        student.nationality || "-";

    const level =
        student.level?.name ||
        student.level?.code ||
        "-";

    /*
     * Dans la structure backend actuelle,
     * la filière est représentée par la classe.
     */
    const filiere =
        student.class?.name ||
        student.class?.code ||
        "-";

    const department =
        student.department?.name ||
        student.department?.code ||
        "-";

    /*
     * ============================================================
     * LIBELLÉS
     * ============================================================
     */

    const informationTitle = t(
        "pages.results.studentInformation",
        {
            defaultValue:
                "Informations de l'étudiant",
        }
    );

    const fullNameLabel = t(
        "common.fields.nomComplet",
        {
            defaultValue: "Nom complet",
        }
    );

    const inscriptionLabel = t(
        "common.fields.numeroInscription",
        {
            defaultValue: "N° d'inscription",
        }
    );

    const nniLabel = t(
        "common.fields.nni",
        {
            defaultValue: "NNI",
        }
    );

    const emailLabel = t(
        "common.fields.email",
        {
            defaultValue: "Email",
        }
    );

    const nationalityLabel = t(
        "common.fields.nationalite",
        {
            defaultValue: "Nationalité",
        }
    );

    const levelLabel = t(
        "common.fields.niveau",
        {
            defaultValue: "Niveau",
        }
    );

    const filiereLabel = t(
        "common.fields.filiere",
        {
            defaultValue: "Filière",
        }
    );

    const departmentLabel = t(
        "common.fields.departement",
        {
            defaultValue: "Département",
        }
    );

    return (
        <View style={styles.studentCard}>

            {/* =================================================
             * TITRE
             * ================================================= */}

            <View
                style={[
                    styles.studentCardHeader,
                    isArabic &&
                    styles.studentCardHeaderRtl,
                ]}
            >
                <Text
                    style={getTextStyle(
                        informationTitle,
                        styles.sectionTitle,
                        true
                    )}
                >
                    {informationTitle}
                </Text>
            </View>

            {/* =================================================
             * GRILLE
             * ================================================= */}

            <View
                style={[
                    styles.studentGrid,
                    isArabic &&
                    styles.studentGridRtl,
                ]}
            >

                {/* NOM COMPLET */}

                <View style={styles.studentField}>
                    <Text
                        style={getTextStyle(
                            fullNameLabel,
                            styles.fieldLabel
                        )}
                    >
                        {fullNameLabel}
                    </Text>

                    <Text
                        style={getTextStyle(
                            studentName,
                            styles.fieldValue,
                            true
                        )}
                    >
                        {studentName}
                    </Text>
                </View>

                {/* N° INSCRIPTION */}

                <View style={styles.studentField}>
                    <Text
                        style={getTextStyle(
                            inscriptionLabel,
                            styles.fieldLabel
                        )}
                    >
                        {inscriptionLabel}
                    </Text>

                    <Text
                        style={getTextStyle(
                            inscriptionNumber,
                            styles.fieldValue
                        )}
                    >
                        {inscriptionNumber}
                    </Text>
                </View>

                {/* NNI */}

                <View style={styles.studentField}>
                    <Text
                        style={getTextStyle(
                            nniLabel,
                            styles.fieldLabel
                        )}
                    >
                        {nniLabel}
                    </Text>

                    <Text
                        style={getTextStyle(
                            nni,
                            styles.fieldValue
                        )}
                    >
                        {nni}
                    </Text>
                </View>

                {/* EMAIL */}

                <View style={styles.studentField}>
                    <Text
                        style={getTextStyle(
                            emailLabel,
                            styles.fieldLabel
                        )}
                    >
                        {emailLabel}
                    </Text>

                    <Text
                        style={getTextStyle(
                            email,
                            styles.fieldValue
                        )}
                    >
                        {email}
                    </Text>
                </View>

                {/* NATIONALITÉ */}

                <View style={styles.studentField}>
                    <Text
                        style={getTextStyle(
                            nationalityLabel,
                            styles.fieldLabel
                        )}
                    >
                        {nationalityLabel}
                    </Text>

                    <Text
                        style={getTextStyle(
                            nationality,
                            styles.fieldValue
                        )}
                    >
                        {nationality}
                    </Text>
                </View>

                {/* NIVEAU */}

                <View style={styles.studentField}>
                    <Text
                        style={getTextStyle(
                            levelLabel,
                            styles.fieldLabel
                        )}
                    >
                        {levelLabel}
                    </Text>

                    <Text
                        style={getTextStyle(
                            level,
                            styles.fieldValue
                        )}
                    >
                        {level}
                    </Text>
                </View>

                {/* FILIÈRE */}

                <View style={styles.studentField}>
                    <Text
                        style={getTextStyle(
                            filiereLabel,
                            styles.fieldLabel
                        )}
                    >
                        {filiereLabel}
                    </Text>

                    <Text
                        style={getTextStyle(
                            filiere,
                            styles.fieldValue
                        )}
                    >
                        {filiere}
                    </Text>
                </View>

                {/* DÉPARTEMENT */}

                <View style={styles.studentFieldWide}>
                    <Text
                        style={getTextStyle(
                            departmentLabel,
                            styles.fieldLabel
                        )}
                    >
                        {departmentLabel}
                    </Text>

                    <Text
                        style={getTextStyle(
                            department,
                            styles.fieldValue
                        )}
                    >
                        {department}
                    </Text>
                </View>

            </View>
        </View>
    );
};

/* =========================================================
 * STATISTIQUES
 * ======================================================= */

const Statistics = ({
    rows,
    t,
    language,
}) => {
    const stats = calculateStats(rows);

    const totalLabel = t(
        "pages.results.totalExams",
        {
            defaultValue: "Examens",
        }
    );

    const passedLabel = t(
        "common.ui.admis",
        {
            defaultValue: "Admis",
        }
    );

    const failedLabel = t(
        "common.ui.nonAdmis",
        {
            defaultValue: "Non admis",
        }
    );

    const averageLabel = t(
        "pages.results.average",
        {
            defaultValue: "Moyenne",
        }
    );

    const successRateLabel = t(
        "pages.results.successRate",
        {
            defaultValue: "Taux de réussite",
        }
    );

    return (
        <View style={styles.statisticsContainer}>
            {/* TOTAL */}

            <View style={styles.statCard}>
                <Text
                    style={getTextStyle(
                        totalLabel,
                        styles.statLabel
                    )}
                >
                    {totalLabel}
                </Text>

                <Text style={styles.statValue}>
                    {formatNumber(
                        stats.total,
                        language,
                        0
                    )}
                </Text>
            </View>

            {/* ADMIS */}

            <View
                style={[
                    styles.statCard,
                    styles.statSuccess,
                ]}
            >
                <Text
                    style={getTextStyle(
                        passedLabel,
                        styles.statLabel
                    )}
                >
                    {passedLabel}
                </Text>

                <Text style={styles.statValue}>
                    {formatNumber(
                        stats.passed,
                        language,
                        0
                    )}
                </Text>
            </View>

            {/* NON ADMIS */}

            <View
                style={[
                    styles.statCard,
                    styles.statDanger,
                ]}
            >
                <Text
                    style={getTextStyle(
                        failedLabel,
                        styles.statLabel
                    )}
                >
                    {failedLabel}
                </Text>

                <Text style={styles.statValue}>
                    {formatNumber(
                        stats.failed,
                        language,
                        0
                    )}
                </Text>
            </View>

            {/* MOYENNE */}

            <View style={styles.statCard}>
                <Text
                    style={getTextStyle(
                        averageLabel,
                        styles.statLabel
                    )}
                >
                    {averageLabel}
                </Text>

                <Text style={styles.statValue}>
                    {formatNumber(
                        stats.average,
                        language,
                        2
                    )}
                    %
                </Text>
            </View>

            {/* TAUX */}

            <View style={styles.statCard}>
                <Text
                    style={getTextStyle(
                        successRateLabel,
                        styles.statLabel
                    )}
                >
                    {successRateLabel}
                </Text>

                <Text style={styles.statValue}>
                    {formatNumber(
                        stats.successRate,
                        language,
                        1
                    )}
                    %
                </Text>
            </View>
        </View>
    );
};

/* =========================================================
 * TABLEAU
 * ======================================================= */

const ResultsTable = ({
    rows = [],
    t,
    language,
    isArabic,
}) => {
    const resultsTitle = t(
        "pages.results.results",
        {
            defaultValue: "Résultats",
        }
    );

    const examenLabel = t(
        "common.ui.examen",
        {
            defaultValue: "Examen",
        }
    );

    const dateLabel = t(
        "common.ui.date",
        {
            defaultValue: "Date",
        }
    );

    const scoreLabel = t(
        "common.ui.score",
        {
            defaultValue: "Score",
        }
    );

    const resultLabel = t(
        "common.ui.resultat",
        {
            defaultValue: "Résultat",
        }
    );

    const noResultsText = t(
        "pages.results.noResults",
        {
            defaultValue:
                "Aucun résultat disponible.",
        }
    );

    const bestResultText = t(
        "pages.results.bestResult",
        {
            defaultValue:
                "Meilleur résultat",
        }
    );

    const passedText = t(
        "common.ui.admis",
        {
            defaultValue: "Admis",
        }
    );

    const failedText = t(
        "common.ui.nonAdmis",
        {
            defaultValue: "Non admis",
        }
    );

    return (
        <View style={styles.tableContainer}>
            {/* TITRE */}

            <Text
                style={getTextStyle(
                    resultsTitle,
                    styles.tableTitle,
                    true
                )}
            >
                {resultsTitle}
            </Text>

            {/* HEADER */}

            <View
                style={[
                    styles.tableHeader,
                    isArabic &&
                    styles.tableHeaderRtl,
                ]}
            >
                <View
                    style={[
                        styles.colExam,
                        styles.headerCell,
                    ]}
                >
                    <Text
                        style={getTextStyle(
                            examenLabel,
                            styles.headerText,
                            true
                        )}
                    >
                        {examenLabel}
                    </Text>
                </View>

                <View
                    style={[
                        styles.colDate,
                        styles.headerCell,
                    ]}
                >
                    <Text
                        style={getTextStyle(
                            dateLabel,
                            styles.headerText,
                            true
                        )}
                    >
                        {dateLabel}
                    </Text>
                </View>

                <View
                    style={[
                        styles.colScore,
                        styles.headerCell,
                    ]}
                >
                    <Text
                        style={getTextStyle(
                            scoreLabel,
                            styles.headerText,
                            true
                        )}
                    >
                        {scoreLabel}
                    </Text>
                </View>

                <View
                    style={[
                        styles.colStatus,
                        styles.headerCell,
                    ]}
                >
                    <Text
                        style={getTextStyle(
                            resultLabel,
                            styles.headerText,
                            true
                        )}
                    >
                        {resultLabel}
                    </Text>
                </View>
            </View>

            {/* LIGNES */}

            {!rows.length ? (
                <View style={styles.emptyRow}>
                    <Text
                        style={getTextStyle(
                            noResultsText,
                            styles.emptyText
                        )}
                    >
                        {noResultsText}
                    </Text>
                </View>
            ) : (
                rows.map((row, index) => {
                    const examTitle =
                        row?.examTitle ||
                        row?.title ||
                        "-";

                    const earnedPoints =
                        row?.totalPointsEarned !== null &&
                            row?.totalPointsEarned !== undefined
                            ? Number(row.totalPointsEarned)
                            : null;

                    const possiblePoints =
                        row?.totalPointsPossible !== null &&
                            row?.totalPointsPossible !== undefined
                            ? Number(row.totalPointsPossible)
                            : null;

                    const score =
                        earnedPoints !== null &&
                            possiblePoints !== null &&
                            Number.isFinite(earnedPoints) &&
                            Number.isFinite(possiblePoints)
                            ? `${formatNumber(earnedPoints, language, 2)} / ${formatNumber(
                                possiblePoints,
                                language,
                                2
                            )}`
                            : "-";

                    const passed = Boolean(
                        row?.passed
                    );

                    const rowDate =
                        row?.date ||
                        row?.corrected_at;

                    return (
                        <View
                            key={`${row?.examId ||
                                row?.exam_id ||
                                "exam"}-${rowDate ||
                                index}-${index}`}
                            wrap={false}
                            style={[
                                styles.tableRow,
                                index % 2 === 1 &&
                                styles.tableRowAlternate,
                                isArabic &&
                                styles.tableRowRtl,
                            ]}
                        >
                            {/* EXAMEN */}

                            <View style={styles.colExam}>
                                <Text
                                    style={getTextStyle(
                                        examTitle,
                                        styles.cellText
                                    )}
                                >
                                    {examTitle}
                                </Text>

                                {row?.isBest && (
                                    <Text
                                        style={getTextStyle(
                                            bestResultText,
                                            styles.bestResult
                                        )}
                                    >
                                        {bestResultText}
                                    </Text>
                                )}
                            </View>

                            {/* DATE */}

                            <View style={styles.colDate}>
                                <Text
                                    style={getTextStyle(
                                        formatDate(
                                            rowDate,
                                            language
                                        ),
                                        styles.cellText
                                    )}
                                >
                                    {formatDate(
                                        rowDate,
                                        language
                                    )}
                                </Text>
                            </View>

                            {/* SCORE */}

                            <View style={styles.colScore}>
                                <Text
                                    style={getTextStyle(
                                        score,
                                        styles.scoreText,
                                        true
                                    )}
                                >
                                    {score}
                                </Text>
                            </View>

                            {/* RESULTAT */}

                            <View
                                style={[
                                    styles.colStatus,
                                    styles.statusCell,
                                ]}
                            >
                                <Badge
                                    variant={
                                        passed
                                            ? "success"
                                            : "danger"
                                    }
                                >
                                    {passed
                                        ? passedText
                                        : failedText}
                                </Badge>
                            </View>
                        </View>
                    );
                })
            )}
        </View>
    );
};

/* =========================================================
 * SECTION VALIDATION
 * ======================================================= */

const ValidationSection = ({
    transcript,
    t,
}) => {
    const rows =
        transcript?.rows || [];

    const stats =
        calculateStats(rows);

    const validationTitle = t(
        "pages.results.validation",
        {
            defaultValue: "Validation",
        }
    );

    const summaryText =
        stats.total > 0
            ? t(
                "pages.results.validationSummary",
                {
                    defaultValue:
                        "Le présent relevé récapitule les résultats enregistrés pour l'étudiant.",
                }
            )
            : t(
                "pages.results.noResults",
                {
                    defaultValue:
                        "Aucun résultat disponible.",
                }
            );

    const signatureLabel = t(
        "pages.results.examResponsible",
        {
            defaultValue:
                "Signature du responsable des examens",
        }
    );

    return (
        <View style={styles.validationSection}>
            <View style={styles.validationHeader}>
                <Text
                    style={getTextStyle(
                        validationTitle,
                        styles.validationTitle,
                        true
                    )}
                >
                    {validationTitle}
                </Text>
            </View>

            <View style={styles.validationContent}>
                <View
                    style={styles.validationTextBlock}
                >
                    <Text
                        style={getTextStyle(
                            summaryText,
                            styles.validationText
                        )}
                    >
                        {summaryText}
                    </Text>
                </View>

                <View
                    style={styles.signatureBox}
                >
                    <Text
                        style={getTextStyle(
                            signatureLabel,
                            styles.signatureLabel
                        )}
                    >
                        {signatureLabel}
                    </Text>

                    <View
                        style={styles.signatureLine}
                    />
                </View>
            </View>
        </View>
    );
};

/* =========================================================
 * FOOTER
 * ======================================================= */

const Footer = ({
    t,
    language,
}) => {
    const generatedText = t(
        "pages.results.generatedDocument",
        {
            defaultValue:
                "Document généré automatiquement.",
        }
    );

    const currentDate =
        formatDate(
            new Date(),
            language
        );

    return (
        <View
            fixed
            style={styles.footer}
        >
            <Text
                style={getTextStyle(
                    generatedText,
                    styles.footerText
                )}
            >
                {generatedText}
            </Text>

            <Text
                style={getTextStyle(
                    currentDate,
                    styles.footerText
                )}
            >
                {currentDate}
            </Text>

            <Text
                style={styles.pageNumber}
                render={({
                    pageNumber,
                    totalPages,
                }) =>
                    `${pageNumber} / ${totalPages}`
                }
            />
        </View>
    );
};

/* =========================================================
 * STYLES
 * ======================================================= */

const COLORS = {
    primary: "#1f4e79",
    primaryDark: "#163a5c",
    secondary: "#5b7083",
    text: "#1f2937",
    muted: "#6b7280",
    border: "#d7dde5",
    background: "#f5f7fa",
    white: "#ffffff",
    success: "#166534",
    successBackground: "#dcfce7",
    danger: "#991b1b",
    dangerBackground: "#fee2e2",
    warning: "#92400e",
    warningBackground: "#fef3c7",
};

const styles = StyleSheet.create({
    /* =======================================================
     * PAGE
     * ===================================================== */

    page: {
        paddingTop: 36,
        paddingBottom: 55,
        paddingHorizontal: 40,
        fontFamily: "LouguiyaFR",
        fontSize: 9,
        color: COLORS.text,
        backgroundColor: COLORS.white,
    },

    arabicPage: {
        fontFamily: "Louguiya",
        direction: "rtl",
    },

    arabicText: {
        fontFamily: "Louguiya",
        direction: "rtl",
        textAlign: "right",
    },

    /* =======================================================
     * HEADER
     * ===================================================== */

    topBar: {
        height: 5,
        backgroundColor: COLORS.primary,
        marginBottom: 12,
    },

    header: {
        width: "100%",
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
        paddingBottom: 12,
    },

    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },

    headerRowRtl: {
        flexDirection: "row-reverse",
    },

    institutionSection: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
    },

    institutionSectionRtl: {
        flexDirection: "row-reverse",
    },

    logo: {
        width: 58,
        height: 58,
        objectFit: "contain",
        marginRight: 14,
    },

    logoRtl: {
        marginRight: 0,
        marginLeft: 14,
    },

    institutionText: {
        flex: 1,
    },

    institutionTextRtl: {
        alignItems: "flex-end",
    },

    institutionName: {
        fontFamily: "LouguiyaFR",
        fontSize: 16,
        color: COLORS.primary,
        marginBottom: 4,
    },

    documentInfo: {
        width: 150,
        alignItems: "flex-end",
    },

    documentInfoRtl: {
        alignItems: "flex-start",
    },

    documentLabel: {
        fontFamily: "LouguiyaFR",
        fontSize: 7,
        color: COLORS.muted,
        marginBottom: 3,
    },

    documentTitle: {
        fontFamily: "LouguiyaFR",
        fontSize: 10,
        color: COLORS.primaryDark,
        marginBottom: 3,
    },

    documentDate: {
        fontFamily: "LouguiyaFR",
        fontSize: 8,
        color: COLORS.muted,
    },

    /* =======================================================
     * TITRE
     * ===================================================== */

    titleSection: {
        marginTop: 18,
        marginBottom: 15,
        alignItems: "center",
    },

    titleSectionRtl: {
        direction: "rtl",
    },

    title: {
        fontFamily: "LouguiyaFR",
        fontSize: 18,
        color: COLORS.primaryDark,
        textAlign: "center",
    },

    academicYear: {
        marginTop: 5,
        fontFamily: "LouguiyaFR",
        fontSize: 9,
        color: COLORS.muted,
    },

    /* =======================================================
     * STUDENT CARD
     * ===================================================== */

    studentCard: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 5,
        marginBottom: 15,
        overflow: "hidden",
    },

    studentCardHeader: {
        paddingVertical: 7,
        paddingHorizontal: 10,
        backgroundColor: COLORS.background,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
    },

    studentCardHeaderRtl: {
        alignItems: "flex-end",
    },

    sectionTitle: {
        fontFamily: "LouguiyaFR",
        fontSize: 10,
        color: COLORS.primaryDark,
    },

    studentGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        padding: 10,
    },

    studentGridRtl: {
        flexDirection: "row-reverse",
    },

    studentGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        padding: 8,
    },

    studentGridRtl: {
        flexDirection: "row-reverse",
    },

    studentField: {
        width: "33.3333%",
        paddingHorizontal: 7,
        paddingVertical: 5,
    },

    fieldLabel: {
        fontFamily: "LouguiyaFR",
        fontSize: 6.5,
        color: COLORS.muted,
        marginBottom: 2,
    },

    fieldValue: {
        fontFamily: "LouguiyaFR",
        fontSize: 8,
        color: COLORS.text,
    },

    studentFieldWide: {
        width: "66.6666%",
        paddingHorizontal: 7,
        paddingVertical: 5,
    },

    /* =======================================================
     * STATISTIQUES
     * ===================================================== */

    statisticsContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 18,
    },

    statCard: {
        flex: 1,
        minHeight: 55,
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 5,
        paddingVertical: 7,
        paddingHorizontal: 5,
        marginHorizontal: 3,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: COLORS.white,
    },

    statSuccess: {
        borderColor: "#86efac",
        backgroundColor: "#f0fdf4",
    },

    statDanger: {
        borderColor: "#fecaca",
        backgroundColor: "#fef2f2",
    },

    statLabel: {
        fontFamily: "LouguiyaFR",
        fontSize: 7,
        color: COLORS.muted,
        textAlign: "center",
        marginBottom: 4,
    },

    statValue: {
        fontFamily: "LouguiyaFR",
        fontWeight: "bold",
        fontSize: 13,
        color: COLORS.primaryDark,
    },

    /* =======================================================
     * TABLE
     * ===================================================== */

    tableContainer: {
        marginBottom: 18,
    },

    tableTitle: {
        fontFamily: "LouguiyaFR",
        fontSize: 11,
        color: COLORS.primaryDark,
        marginBottom: 7,
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: COLORS.primary,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
        minHeight: 30,
    },

    tableHeaderRtl: {
        flexDirection: "row-reverse",
    },

    headerCell: {
        justifyContent: "center",
    },

    headerText: {
        fontFamily: "LouguiyaFR",
        fontWeight: "bold",
        fontSize: 7.5,
        color: COLORS.white,
        textAlign: "center",
    },

    tableRow: {
        flexDirection: "row",
        minHeight: 37,
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: COLORS.border,
        alignItems: "center",
    },

    tableRowRtl: {
        flexDirection: "row-reverse",
    },

    tableRowAlternate: {
        backgroundColor: "#fafbfc",
    },

    colExam: {
        width: "53%",
        paddingHorizontal: 7,
    },

    colDate: {
        width: "15%",
        paddingHorizontal: 5,
        alignItems: "center",
    },

    colScore: {
        width: "14%",
        paddingHorizontal: 5,
        alignItems: "center",
    },

    colStatus: {
        width: "18%",
        paddingHorizontal: 4,
        alignItems: "center",
    },

    cellText: {
        fontFamily: "LouguiyaFR",
        fontSize: 8,
        color: COLORS.text,
    },

    scoreText: {
        fontFamily: "LouguiyaFR",
        fontWeight: "bold",
        fontSize: 8.5,
        color: COLORS.primaryDark,
    },

    bestResult: {
        fontFamily: "LouguiyaFR",
        fontSize: 6.5,
        color: COLORS.primary,
        marginTop: 3,
    },

    statusCell: {
        justifyContent: "center",
    },

    emptyRow: {
        minHeight: 55,
        borderWidth: 1,
        borderTopWidth: 0,
        borderColor: COLORS.border,
        alignItems: "center",
        justifyContent: "center",
    },

    emptyText: {
        fontFamily: "LouguiyaFR",
        fontSize: 9,
        color: COLORS.muted,
    },

    /* =======================================================
     * BADGES
     * ===================================================== */

    badge: {
        borderRadius: 10,
        paddingVertical: 3,
        paddingHorizontal: 6,
        minWidth: 55,
        alignItems: "center",
        justifyContent: "center",
    },

    badgeText: {
        fontFamily: "LouguiyaFR",
        fontWeight: "bold",
        fontSize: 6.5,
        textAlign: "center",
    },

    badgeDefault: {
        backgroundColor: COLORS.background,
    },

    badgeSuccess: {
        backgroundColor: COLORS.successBackground,
    },

    badgeDanger: {
        backgroundColor: COLORS.dangerBackground,
    },

    badgeWarning: {
        backgroundColor: COLORS.warningBackground,
    },

    /* =======================================================
     * VALIDATION
     * ===================================================== */

    validationSection: {
        borderWidth: 1,
        borderColor: COLORS.border,
        borderRadius: 5,
        marginTop: 5,
        marginBottom: 20,
        overflow: "hidden",
    },

    validationHeader: {
        backgroundColor: COLORS.background,
        paddingVertical: 7,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: COLORS.border,
    },

    validationTitle: {
        fontFamily: "LouguiyaFR",
        fontSize: 10,
        color: COLORS.primaryDark,
    },

    validationContent: {
        padding: 12,
        flexDirection: "row",
        justifyContent: "space-between",
    },

    validationTextBlock: {
        width: "55%",
        paddingRight: 15,
    },

    validationText: {
        fontFamily: "LouguiyaFR",
        fontSize: 8,
        color: COLORS.text,
        lineHeight: 1.5,
    },

    signatureBox: {
        width: "35%",
        alignItems: "center",
        justifyContent: "flex-end",
        minHeight: 55,
    },

    signatureLabel: {
        fontFamily: "LouguiyaFR",
        fontSize: 7,
        color: COLORS.muted,
        textAlign: "center",
        marginBottom: 20,
    },

    signatureLine: {
        width: "90%",
        borderBottomWidth: 1,
        borderBottomColor: COLORS.text,
    },

    /* =======================================================
     * FOOTER
     * ===================================================== */

    footer: {
        position: "absolute",
        bottom: 25,
        left: 40,
        right: 40,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: COLORS.border,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    footerText: {
        fontFamily: "LouguiyaFR",
        fontSize: 6.5,
        color: COLORS.muted,
    },

    pageNumber: {
        fontFamily: "LouguiyaFR",
        fontSize: 6.5,
        color: COLORS.muted,
    },
});

/* =========================================================
 * DOCUMENT PRINCIPAL
 * ======================================================= */

const TranscriptPDF = ({
    transcript = {},
}) => {
    const { t, i18n } =
        useTranslation();

    const language =
        i18n.language || "fr";

    const isArabic =
        isArabicLanguage(language);

    const rows = Array.isArray(
        transcript?.rows
    )
        ? transcript.rows
        : [];

    const documentTitle = t(
        "pages.results.releve",
        {
            defaultValue:
                "Relevé de notes",
        }
    );

    return (
        <Document
            title={documentTitle}
            author="ENSSS"
            subject={documentTitle}
            creator="ENSSS"
        >
            <Page
                size="A4"
                style={[
                    styles.page,
                    isArabic &&
                    styles.arabicPage,
                ]}
            >
                <TranscriptHeader
                    transcript={transcript}
                    t={t}
                    language={language}
                    isArabic={isArabic}
                />

                <StudentCard
                    transcript={transcript}
                    t={t}
                    isArabic={isArabic}
                />

                <Statistics
                    rows={rows}
                    t={t}
                    language={language}
                />

                <ResultsTable
                    rows={rows}
                    t={t}
                    language={language}
                    isArabic={isArabic}
                />

                <ValidationSection
                    transcript={transcript}
                    t={t}
                />

                <Footer
                    t={t}
                    language={language}
                />
            </Page>
        </Document>
    );
};

export default TranscriptPDF;